// Shared wording for a Clerk SignIn that did not complete.
//
// Extracted from SignInScreen (JAR-1481) because ForgotPasswordScreen needs the
// same two answers and was inventing its own. Two screens describing one SDK's
// statuses in two vocabularies is how one of them goes stale: SignInScreen was
// corrected twice (JAR-1352, JAR-1391) and ForgotPasswordScreen received neither
// correction, because nothing connected them.
//
// This file is the PURE half: wording only. The stateful half, entering the
// second factor and the app's one attemptSecondFactor call, used to be stuck
// inside SignInScreen as a closure over its state, which is why the reset screen
// could not finish an MFA sign-in. Since JAR-1537 it lives in useSecondFactor,
// shared by both screens, with SecondFactorPanel as its form. A second copy of
// that panel is still the thing to avoid: it is the choke point the
// JAR-1391/JAR-1445 pattern exists for.

import type { SignInStatus } from '@clerk/shared/types';

// Said wherever a sign-in step is asked for before Clerk has loaded.
export const SIGN_IN_STILL_LOADING = 'Sign-in is still loading. Try again in a moment.';

// describeIncompleteStatus turns a non-complete Clerk SignIn status into
// something true (JAR-1352).
//
// Every one of them used to produce "Additional verification is required. Check
// your email." — which sent no email, because nothing on that path calls any
// prepare* method, and offered no way to continue. A user with the CORRECT
// password was told to check an inbox that would never receive anything.
//
// The messages below say what is actually happening and, where the app has a
// route forward, point at it. `needs_new_password` is not here: it is handled,
// not described. Neither is `needs_second_factor`, as of JAR-1391 — it used to
// say "this app cannot complete yet, contact support", which was true when it
// was written and is no longer.
//
// 'complete' IS NOT ACCEPTED, by type. Every caller renders the result as an
// error, so there is no true sentence to return for a sign-in that succeeded:
// "Sign-in could not be completed (status: complete)" would report a success as
// a failure, and any friendlier wording would still arrive in a red error slot.
// The caller must not get here, and now it cannot compile if it does (web-app#370
// review). A status Clerk adds later still reaches `default` at runtime, which is
// what the named fallback below is for.
export function describeIncompleteStatus(status: Exclude<SignInStatus, 'complete'> | null | undefined): string {
  switch (status) {
    case 'needs_first_factor':
      return 'That password was accepted but this account needs a different sign-in method. Use “Forgot password?” to sign in with an emailed code.';
    case 'needs_identifier':
      return 'Sign-in did not receive an email address. Please try again.';
    default:
      // Named, not swallowed. An unrecognised status is a Clerk change or a
      // configuration we have not seen, and the person reading the screenshot
      // needs the word Clerk actually returned.
      return `Sign-in could not be completed (status: ${status ?? 'unknown'}). Please contact support.`;
  }
}

// The second factors this app can complete are `totp`, `backup_code`,
// `email_code` (Device Trust's new-device check, JAR-1708) and, since JAR-2872,
// `phone_code` — the SMS factor an account enrols at setup. `email_link` still
// cannot be completed here: a link is a redirect flow rather than a code. Those
// accounts get an honest refusal that names the factor, rather than a prompt
// for a code that was never sent (web-app#305 review).
export function describeUnsupportedSecondFactor(strategies: string[]): string {
  const named = strategies.length ? ` (${strategies.join(', ')})` : '';
  return `This account uses a sign-in step this app can't complete yet${named}. Contact support.`;
}
